import { dist } from "vector";
import get from "lodash/get.js";
import { hasCutFabrication, hasNonRectShape } from "./main.js";
import formatEdgeTreatment from "@local/extensions/formatters/edge-treatment.js";
import { computeLinearTotals } from "./lib/linear-totals.js";
import { gridlinePositions } from "./entities/ref-planes.js";

import type { TablesGenerated } from "./main.js";
import type { ItemRecordCore } from "./entities/item.js";
import type { HydratedGroup } from "./main.js";

type Item = TablesGenerated<"items"> & ItemRecordCore;
type Type = TablesGenerated<"types">;
type Group = TablesGenerated<"groups"> & HydratedGroup;
type ItemReport = TablesGenerated<"item_reports">;
type DocumentNote = TablesGenerated<"document_notes">;
type ItemFilter = {
  field: string;
  operator: "is_equal_to" | "is_not_equal_to" | "contains" | "is_one_of";
  value: string;
}

function filterItems(items: Item[] | DocumentNote[], itemFilter: ItemFilter | null) {
  if (!itemFilter?.field || !itemFilter?.operator || !itemFilter?.value)
    return items;

  return items.filter((item) => {
    const prop = get(item, itemFilter.field);
    if (itemFilter.operator === "is_equal_to") return prop === itemFilter.value;
    if (itemFilter.operator === "is_not_equal_to")
      return prop !== itemFilter.value;
    if (itemFilter.operator === "contains") {
      return (
        prop &&
        prop.toString().toLowerCase().includes(itemFilter.value.toLowerCase())
      );
    }
    if (itemFilter.operator === "is_one_of") {
      return itemFilter.value?.includes(prop);
    }
    if (itemFilter.operator === "is_not_one_of") {
      return !itemFilter.value?.includes(prop);
    }
  });
}

function filterCollections(collections: Item[], items: Item[], collectionFilter: ItemFilter | null, report: ItemReport) {
  if (report.show_categories_with_no_items) return filterItems(collections, collectionFilter);

  const presentCollections = items.reduce((acc, item) => {
    if (item.collection_id != undefined) {
      acc[item.collection_id] = true;
    }
    return acc;
  }, {});

  return filterItems(collections, collectionFilter).filter((c) => presentCollections[c.id]);
}

const etIndex = {
  "7f024416-0921-50a0-9b35-58beed08cb76": {
    "name": "Cut only",
  },
  "b64f8837-f724-51c6-9629-01c93ff3bce0": {
    "name": "Cut and swipe",
  },
  "1ba8f756-7dc8-58ac-bd60-5e37fc56fa41": {
    "name": "Grind and chamfer",
  },
  "70bff4fa-c9c1-5aa9-8e5a-538e226640f0": {
    "name": "Pencil grind",
  },
  "7f5c23a4-7bed-581d-80a9-0ae5cfab4849": {
    "name": "Pencil polish",
  },
  "de9dc1f0-6e4b-5de6-a9ad-619effc58a65": {
    "name": "Flat polish",
  }
}

function filterTypes(types: Type[], items: Item[], report: ItemReport) {
  if (report.show_categories_with_no_items) return types;

  const presentTypes = items.reduce((acc, item) => {
    if (item.type_id != undefined) {
      acc[item.type_id] = true;
    }
    return acc;
  }, {});

  return types.filter((t) => presentTypes[t.id]);
}

function itemsInCategories(items: Item[], collections: Item[], types: Type[], report: ItemReport) {
  if (report.group_by === "opening") {
    const presentCollections = collections.reduce((acc, c) => {
      acc[c.id] = true;
      return acc;
    }, {});

    return items.filter((item) => {
      if (item.collection_id) {
        return presentCollections[item.collection_id];
      }

      return report.show_untyped_items;
    });
  } else {
    const presentTypes = types.reduce((acc, t) => {
      acc[t.id] = true;
      return acc;
    }, {});

    return items.filter((item) => {
      if (item.type_id) {
        return presentTypes[item.type_id];
      }

      return report.show_untyped_items;
    });
  }
}

function countBySheetSection(items: Item[], group: Group, docId: string | null, sheetSection: string[] | null) {
  const doc = docId ? group.documents[docId] : null;
  const section = sheetSection;

  const quantities = items.reduce((acc, item) => {
    acc[item.id] = 0;
    return acc;
  }, {} as Record<string, number>);

  if (!doc || !section) {
    return quantities;
  }

  const sectionPages = doc.data.pages.order.reduce((acc, pageId) => {
    const page = doc.data.pages[pageId];
    if (section?.includes(page.section)) {
      acc[pageId] = true;
    }
    return acc;
  }, {} as Record<string, true>);

  group.locations.order.forEach((locId) => {
    const loc = group.locations[locId];
    if (
      sectionPages[loc.page_id] &&
      quantities[loc.record_id] !== undefined &&
      !loc.ignore_for_auto_count
    ) {
      quantities[loc.record_id] += 1;
    }
  });

  return quantities;
}

function countByLocationCategory(items: Item[], group: Group, docId: string | null, category: string[] | null) {
  const doc = docId ? group.documents[docId] : null;

  const quantities = items.reduce((acc, item) => {
    acc[item.id] = 0;
    return acc;
  }, {} as Record<string, number>);

  if (!doc || !category) {
    return quantities;
  }

  group.locations.order.forEach((locId) => {
    const loc = group.locations[locId];
    if (
      quantities[loc.record_id] !== undefined &&
      loc.document_id === doc.id &&
      category.includes(loc.category) &&
      !loc.ignore_for_auto_count
    ) {
      quantities[loc.record_id] += 1;
    }
  });

  return quantities;
}

function countItems(items: Item[], group: Group, report: ItemReport | null = null) {
  if (report?.count_method === "sheet_section") {
    return countBySheetSection(items, group, report?.count_document_id, report?.count_sheet_section);
  } else if (report?.count_method === "location_category") {
    return countByLocationCategory(items, group, report?.count_document_id, report?.count_location_category);
  } else {
    return items.reduce((acc, item) => {
      acc[item.id] = item.quantity;
      return acc;
    }, {} as Record<string, number | null>);
  }
}

function countCollections(items: Item[], group: Group, report: ItemReport | null = null) {
  if (report?.collection_count_method === "sheet_section") {
    return countBySheetSection(items, group, report?.collection_count_document_id, report?.collection_count_sheet_section);
  } else if (report?.collection_count_method === "location_category") {
    return countByLocationCategory(items, group, report?.collection_count_document_id, report?.collection_count_location_category);
  } else {
    return items.reduce((acc, item) => {
      acc[item.id] = item.quantity;
      return acc;
    }, {} as Record<string, number | null>);
  }
}


function groupItems(items: Item[], field = "type_id") {
  const grouped = items.reduce(
    (all, item) => {
      const v = get(item, field);

      if (v) {
        if (!all[v]) all[v] = [];
        all[v].push(item);
      } else {
        all.untyped.push(item);
      }

      return all;
    },
    { untyped: [] } as Record<string, Item[]>,
  );

  return grouped;
}

type Section = {
  id: string,
  type: "opening" | "type" | "untyped" | "notes",
  record: Type | Item | null;
  rows: Object[];
  units: number;
  block_area: number;
  item_area: number;
  weight: number;
  edge_treatment: number;
  perimeter: number;
  has_custom_shape: boolean;
  index?: number,
  quantity?: number,
  total_width: number,
  total_height: number,
}

function sectionProps(items: Item[], quantities: number[]) {
  let units = 0;
  let item_area = 0;
  let block_area = 0;
  let perimeter = 0;
  let edge_treatment = 0;
  let total_width = 0;
  let total_height = 0;
  let weight = 0;
  let undefined_items = 0;
  items.forEach((item, i) => {
    const quantity = quantities[i] || 0;
    units += quantity;
    if (item.width && item.height) {
      item_area += item.cache.area_each * quantity;
      block_area += (item.cache.width / 12) * (item.cache.height / 12) * quantity;
    } else {
      undefined_items += 1;
    }
    weight += (item.cache.weight_each || 0) * quantity;

    const et = item.data?.fabrications?.edge_treatment;
    if (et) {
      let itemEt = 0;
      et.forEach((treatment, i) => {
        const edge = item.cache.edges[0][i];
        if (treatment && edge) {
          itemEt += dist(edge.start, edge.end);
        }
      });
      edge_treatment += itemEt * quantity;
    }

    if (item.width) {
      total_width += item.cache.width * quantity;
    }

    if (item.height) {
      total_height += item.cache.height * quantity;
    }

    if (item.width && item.height) {
      perimeter += item.cache.perimeter_in * quantity;
    }
  });

  return { units, item_area, block_area, perimeter, edge_treatment, weight, undefined_items, total_width, total_height };
}

type Column = {
  label: string;
  prop: string;
  type: string;
  id?: string;
  formatterHTML?: Function;
  optionMap?: Record<string, string>;
  formatter?: Function;
}

function makeRow(item: Item, columns: Column[], quantity: number = 0) {
  const area = item.cache.area_each == undefined
    ? ""
    : item.cache.area_each * quantity;
  const weight = item.cache.weight_each == undefined
    ? ""
    : item.cache.weight_each * quantity;

  return columns.reduce((acc, col) => {
    let val;
    if (col.prop === "quantity") {
      val = quantity;
    } else if (col.prop === "cache.area" || col.prop === "area") {
      val = area;
    } else if (col.prop === "cache.weight" || col.prop === "weight") {
      val = weight;
    } else {
      val = get(item, col.prop);
    }

    let result;
    if (col.type === "select") {
      result = col.optionMap[val];
    } else if (col.formatterHTML) {
      result = { html: col.formatterHTML(val) };
    } else if (col.formatter) {
      result = col.formatter(val, item);
    } else {
      result = val;
    }

    if (result === undefined || result === null) result = "";

    acc[col.prop] = result;

    return acc;
  }, {
    quantity,
    area,
    weight,
    item_id: item.id,
    is_custom_shape: hasCutFabrication(item) || hasNonRectShape(item),
    edge_treatment: formatEdgeTreatment(item.data?.fabrications?.edge_treatment || [], etIndex)
  } as Record<string, any>);
}

const hiddenColumnProps = {
  weight: "cache.weight",
  area: "cache.area",
}

function prepareReportData(
  items: Item[],
  collections: Item[],
  types: Type[],
  documentNotes: DocumentNote[],
  group: Group,
  report: ItemReport | null,
  columns: Column[],
) {
  let grouped;
  let cats;
  let showUntyped = true;
  let quants;
  let collQuants;
  if (!report) {
    grouped = groupItems(items, "type_id");
    cats = types;
    quants = countItems(items, group);
    collQuants = countItems(collections, group);
  } else if (report.group_by === "notes") {
    const locsByRecord = group.locations.order.reduce((acc: Record<string, boolean>, locId) => {
      const loc = group.locations[locId];
      if (loc?.record_type !== "document_note") return acc;
      acc[loc.record_id] = true;
      return acc;
    }, {} as Record<string, boolean>);
    const filteredNotes = filterItems(documentNotes, report.note_filter)
      .filter((note) => locsByRecord[note.id]);

    const rows = filteredNotes.map((note) => {
      return {
        id: note.id,
        mark: note.mark,
        title: note.title,
        body: note.body || "",
        scope: note.scope,
        approval_status: note.approval_status,
      }
    });
    return {
      columns: [
        { label: "Mark", prop: "mark" },
        { label: "Title", prop: "title" },
        ...(report?.show_note_body ? [{ label: "Body", prop: "body" }] : []),
        { label: "Flag", prop: "approval_status" },
      ],
      totals: {
        sections_count: 1,
        units: filteredNotes.length,
        block_area: 0,
        item_area: 0,
        weight: 0,
        edge_treatment: 0,
        perimeter: 0,
        collection_area: 0,
        collection_count: 0,
        total_width: 0,
        total_height: 0,
        has_custom_shape: false,
        has_edge_treatment: false,
      },
      sections: [
        {
          id: "notes",
          type: "notes",
          record: null,
          rows,
          units: filteredNotes.length,
          block_area: 0,
          item_area: 0,
          weight: 0,
          edge_treatment: 0,
          perimeter: 0,
          has_custom_shape: false,
          total_width: 0,
          total_height: 0,
          index: 0,
          quantity: filteredNotes.length,
        }
      ]
    }
  } else if (report.group_by === "linear") {
    const openings = filterItems(collections, report.collection_filter);
    quants = countItems(openings, group);
    const display_unit = group.data.settings?.display_unit === "millimeters" ? "mm" : "in";

    let block_area = 0;
    let collection_area = 0;
    let collection_count = 0;
    let total_width = 0;
    let total_height = 0;
    const quantities = openings.map((i) => quants[i.id] || 0);

    const tots = openings.reduce((total, item, i) => {
      if (!item.width || !item.height) return total;
      const quantity = quantities[i] || 0;
      collection_count += quantity;
      block_area += (item.cache.width / 12) * (item.cache.height / 12) * quantity;
      collection_area += (item.cache.width / 12) * (item.cache.height / 12) * quantity;
      total_width += item.cache.width * quantity;
      total_height += item.cache.height * quantity;

      const hors = item.data.reference_planes ? gridlinePositions(item.data.reference_planes, "horizontal", item.cache.bbox.xmin) : [];
      const vers = item.data.reference_planes ? gridlinePositions(item.data.reference_planes, "vertical", item.cache.bbox.ymin) : [];
      const it = computeLinearTotals(item.cache.vertices, hors, vers, item.cache.mergeStore);

      return {
        total_perimeter: total.total_perimeter + it.total_perimeter * quantity,
        vertical_perimeter_length: total.vertical_perimeter_length + it.vertical_perimeter_length * quantity,
        horizontal_perimeter_length: total.horizontal_perimeter_length + it.horizontal_perimeter_length * quantity,
        raked_perimeter_length: total.raked_perimeter_length + it.raked_perimeter_length * quantity,
        vertical_gridline_length: total.vertical_gridline_length + it.vertical_gridline_length * quantity,
        horizontal_gridline_length: total.horizontal_gridline_length + it.horizontal_gridline_length * quantity,
        total_gridline_length: total.total_gridline_length + it.total_gridline_length * quantity,
      }
    }, {
      total_perimeter: 0,
      vertical_perimeter_length: 0,
      horizontal_perimeter_length: 0,
      raked_perimeter_length: 0,
      vertical_gridline_length: 0,
      horizontal_gridline_length: 0,
      total_gridline_length: 0,
    });

    return {
      columns: [
        { label: "", prop: "label" },
        { label: `Length (${display_unit})`, prop: "value" },
      ],
      totals: {
        units: openings.length,
        collection_count,
        block_area,
        item_area: 0,
        weight: 0,
        edge_treatment: 0,
        perimeter: tots.total_perimeter,
        collection_area,
        has_custom_shape: false,
        total_width,
        total_height,
        index: 0,
        quantity: openings.length,
        total_length: tots.total_perimeter + tots.total_gridline_length,
      },
      sections: [
        {
          id: "linear",
          type: "linear",
          record: null,
          rows: [
            { label: "Vertical Perimeter", value: tots.vertical_perimeter_length },
            { label: "Horizontal Perimeter", value: tots.horizontal_perimeter_length },
            { label: "Raked Perimeter", value: tots.raked_perimeter_length },
          ],
          total_value: tots.total_perimeter,
          units: openings.length,
          collection_count,
          block_area,
          item_area: 0,
          weight: 0,
          edge_treatment: 0,
          perimeter: tots.total_perimeter,
          collection_area,
          has_custom_shape: false,
          total_width,
          total_height,
          index: 0,
          quantity: openings.length,
        },
        {
          id: "linear",
          type: "linear",
          record: null,
          rows: [
            { label: "Vertical Interior Gridlines", value: tots.vertical_gridline_length },
            { label: "Horizontal Interior Gridlines", value: tots.horizontal_gridline_length },
          ],
          total_value: tots.total_gridline_length,
          units: openings.length,
          collection_count,
          block_area,
          item_area: 0,
          weight: 0,
          edge_treatment: 0,
          perimeter: tots.total_perimeter,
          collection_area,
          has_custom_shape: false,
          total_width,
          total_height,
          index: 0,
          quantity: openings.length,
        }
      ],
    }
  } else {
    const prefilteredItems = filterItems(items, report.item_filter);
    const filteredCollections = filterCollections(collections, prefilteredItems, report.collection_filter, report);
    const filteredTypes = filterTypes(types, prefilteredItems, report);
    const filteredItems = itemsInCategories(prefilteredItems, filteredCollections, filteredTypes, report);
    const group_by = report.group_by === "opening" ? "collection_id" : "type_id";
    grouped = groupItems(filteredItems, group_by);
    cats = report.group_by === "opening" ? filteredCollections : filteredTypes;
    showUntyped = report.show_untyped_items;
    quants = countItems(filteredItems, group, report);
    collQuants = countCollections(filteredCollections, group, report);
  }

  const cols = columns
    .filter((col) => {
      const prop = hiddenColumnProps[col.prop] ?? col.prop;
      if (report?.hidden_columns[prop]) return false;
      if (col.id != undefined && report?.hidden_columns[col.id]) return false;
      return true;
    });

  const sections = cats.map((cat) => {
    const items = (grouped[cat.id] || []);
    let quantities = items.map((i) => quants[i.id] || 0);
    let quantity = cat?.quantity;
    if (report?.group_by === "opening") {
      quantity = collQuants[cat.id] || 0;
    }
    if (report?.multiply_contained_item_counts && report?.count_method !== "direct_entry") {
      if (report?.group_by === "opening") {
        quantities = quantities.map((q) => q * quantity);
      } else {
        quantities = quantities.map((q, i) => {
          const item = items[i];
          const cq = collQuants[item.collection_id || ""] || 0;
          return q * cq;
        });
      }
    }
    const rows = (grouped[cat.id] || []).map((item, i) => makeRow(item, cols, quantities[i]));
    const has_custom_shape = rows.some((r) => r.is_custom_shape);

    return {
      id: cat.id,
      type: report?.group_by,
      record: cat,
      rows,
      has_custom_shape,
      quantity,
      ...sectionProps(items, quantities),
    } as Section;
  });

  if (showUntyped && grouped.untyped?.length) {
    const quantities = grouped.untyped.map((i) => quants[i.id] || 0);
    const rows = grouped.untyped.map((item, i) => makeRow(item, columns, quantities[i]));
    const has_custom_shape = rows.some((r) => r.is_custom_shape);

    sections.push({
      id: "untyped",
      type: "untyped",
      record: null,
      rows,
      has_custom_shape,
      ...sectionProps(grouped.untyped, quantities),
    });
  }

  let index = 0;
  sections.forEach((section) => {
    index += section.rows.length;
    section.index = index;
  });

  // Totals
  let sections_count = cats.length;
  let units = 0;
  let item_area = 0;
  let block_area = 0;
  let collection_area = 0;
  let collection_count = 0;
  let edge_treatment = 0;
  let perimeter = 0;
  let weight = 0;
  let total_width = 0;
  let total_height = 0;
  let has_custom_shape = false;
  let has_edge_treatment = false;

  sections.forEach((section) => {
    if (report?.group_by === "opening" && section.record) {
      const o = section.record as Item;
      if (o.width && o.height) {
        collection_area += (o.cache.width / 12) * (o.cache.height / 12) * o.quantity;
      }
    }

    if (section.has_custom_shape) {
      has_custom_shape = true;
    }

    if (section.edge_treatment > 0) {
      has_edge_treatment = true;
    }

    collection_count += section.quantity || 0;
    units += section.units;
    item_area += section.item_area;
    block_area += section.block_area;
    edge_treatment += section.edge_treatment;
    perimeter += section.perimeter;
    weight += section.weight;
    total_width += section.total_width;
    total_height += section.total_height;
  });

  return {
    totals: {
      sections_count,
      units,
      item_area,
      block_area,
      collection_area,
      collection_count,
      edge_treatment,
      perimeter,
      weight,
      total_width,
      total_height,
      has_custom_shape,
      has_edge_treatment,
    },
    columns: cols,
    sections,
  }
}

export { prepareReportData };
